import PropTypes from "prop-types";
import { memo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { Amount } from "app/components/ui/Amount/Amount";
import FloatingButton from "app/pages/.shared/Popover/FloatingButton";
import SmartDPProductPricingInfo from "app/pages/SmartDP/Listing/SmartDPProduct/SmartDPProductPricingInfo/SmartDPProductPricingInfo";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import MilesEarnedContainer from "app/pages/.shared/MilesEarned/MilesEarnedContainer";
import "./SdpProductPricingBlock.scss";

const SdpProductPricingBlock = ({
	isStopOver = false,
	accommodation,
	showDiscountedAccommodation = false,
	discountedPercentage = 0,
	publicPrice = 0,
	discountValue = 0,
	price,
}) => {
	const intl = useIntl();
	const { track } = useAnalytics();

	const trackData = { boardCode: accommodation?.boardCode, discountValue, publicPrice };

	return (
		<div className="sdp-product-pricing-block">
			<div className="sdp-product-pricing-block__values">
				{isStopOver && (
					<SmartDPProductPricingInfo
						basePrice={accommodation?.basePrice}
						total={accommodation?.total}
						upgradePrice={accommodation?.upgradePrice}
					/>
				)}
				<Amount
					value={accommodation.total}
					perPerson
					size="xl3"
					suffixSize="small"
					data-testid="amount"
				/>
				{showDiscountedAccommodation && discountedPercentage !== 0 && (
					<Typography
						className="sdp-product-pricing-block__discount-percentage"
						data-testid="discount-percentage"
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						component="p"
					>
						-{discountedPercentage}%
					</Typography>
				)}
			</div>
			{showDiscountedAccommodation && publicPrice !== 0 && (
				<FloatingButton
					position="bottom"
					floatingContainerClassName="sdp-product-pricing-block__floating-container"
					referenceComponent={
						<Typography
							variant={TYPOGRAPHY_VARIANTS.SMALL}
							className="sdp-product-pricing-block__reference"
							onClick={() =>
								track("discounted_product_click_reference_price", trackData)
							}
							onMouseEnter={() =>
								track("discounted_product_hover_reference_price", trackData)
							}
						>
							<FormattedMessage
								id="general.instead.of.price.with.amount"
								values={{
									price: (
										<Amount value={publicPrice} isBold={false} size="small" />
									),
								}}
							/>
						</Typography>
					}
					floatingContent={
						<Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
							{intl.formatMessage({
								id: "general.instead.of.price.with.amount.description",
							})}
						</Typography>
					}
					popoverClassName="sdp-product-pricing-block__popover"
					dismissOnFloatingClick
					trigger="hover"
				/>
			)}
			<MilesEarnedContainer price={price} />
		</div>
	);
};

SdpProductPricingBlock.propTypes = {
	isStopOver: PropTypes.bool,
	accommodation: PropTypes.shape({
		basePrice: PropTypes.shape({
			label: PropTypes.string,
			amount: PropTypes.number,
		}),
		total: PropTypes.number,
		upgradePrice: PropTypes.arrayOf(
			PropTypes.shape({
				label: PropTypes.string,
				amount: PropTypes.number,
			})
		),
		boardCode: PropTypes.string,
	}).isRequired,
	showDiscountedAccommodation: PropTypes.bool,
	discountedPercentage: PropTypes.number,
	publicPrice: PropTypes.number,
	discountValue: PropTypes.number,
	price: PropTypes.number,
};

export default memo(SdpProductPricingBlock);
